<script lang="ts">
	// UC-18, edit in place: one saved address opened into its fields, on the
	// till's customer sheet and on the dashboard's customer (Patterns, "edit
	// in place"). The address, the landmark and the area change; the area
	// comes from the shop the address belongs to and is required when that
	// shop has areas. Cancel and Save address act on this row only, apart
	// from the customer's name.
	//
	// An address the customer already has is refused in the address field,
	// and Save stays off until the address changes.

	import { untrack } from 'svelte';
	import Button from '../components/Button.svelte';
	import Choice from '../components/Choice.svelte';
	import Field from '../components/Field.svelte';
	import { message, type Language } from '../i18n/messages';
	import { formatMinor } from '../format/money';
	import type { AddressEdit, AddressEditOutcome } from '../till/customers.svelte';
	import type { components } from '../api/types';

	type Area = components['schemas']['AreaBody'];

	interface Props {
		language: Language;
		minorDigits: number;
		testId: string;
		address: string;
		landmark: string;
		areaId: string;
		/** The areas of the address's shop. None: the area is not asked. */
		areas: Area[];
		areasTitle: string;
		size: 'md' | 'sm';
		onCancel: () => void;
		onSave: (edit: AddressEdit) => Promise<AddressEditOutcome>;
	}

	let {
		language,
		minorDigits,
		testId,
		address,
		landmark,
		areaId,
		areas,
		areasTitle,
		size,
		onCancel,
		onSave,
	}: Props = $props();

	// The row's own draft, from what is saved when it opens: the caller
	// mounts one row's editor at a time, so later changes to the saved
	// address are not this draft's business.
	let draft = $state(
		untrack(() => ({
			address,
			landmark,
			areaId: areas.some((area) => area.id === areaId) ? areaId : '',
		})),
	);
	let taken = $state('');
	let saving = $state(false);

	const error = $derived(
		!draft.address.trim()
			? message(language, 'customerAddressRequired')
			: taken && taken === draft.address.trim()
				? message(language, 'customerAddressTaken')
				: undefined,
	);
	const saveOff = $derived(!!error || (areas.length > 0 && !draft.areaId) || saving);

	const areaOptions = $derived(
		areas.map((area) => ({
			value: area.id,
			label: area.name,
			price: formatMinor(area.feeMinor, minorDigits),
			testId: `${testId}-area-${area.id}`,
		})),
	);

	async function save() {
		if (saveOff) return;
		saving = true;
		const result = await onSave({
			address: draft.address,
			landmark: draft.landmark,
			deliveryAreaId: draft.areaId,
		});
		saving = false;
		if (result === 'taken') taken = draft.address.trim();
	}
</script>

<div class="edit" data-testid={testId}>
	<div class="fields" class:wide={size === 'md'}>
		<Field
			testId="{testId}-address"
			label={message(language, 'deliveryAddress')}
			value={draft.address}
			maxLength={500}
			{error}
			errorTestId="{testId}-error"
			onInput={(value) => (draft.address = value)}
		/>
		<Field
			testId="{testId}-landmark"
			label={message(language, 'deliveryLandmark')}
			value={draft.landmark}
			maxLength={200}
			placeholder={message(language, 'deliveryLandmarkHint')}
			onInput={(value) => (draft.landmark = value)}
		/>
	</div>
	{#if areas.length > 0}
		<Choice
			testId="{testId}-areas"
			title={areasTitle}
			rule={message(language, 'deliveryRequired')}
			options={areaOptions}
			value={draft.areaId}
			onChange={(picked) => (draft.areaId = String(picked))}
		/>
	{/if}
	<div class="actions">
		<Button
			testId="{testId}-cancel"
			label={message(language, 'cancel')}
			tone="text-quiet"
			{size}
			disabled={saving}
			onPress={onCancel}
		/>
		<Button
			testId="{testId}-save"
			label={message(language, 'customerSaveAddress')}
			tone="primary"
			{size}
			disabled={saveOff}
			state={saving ? 'pending' : 'idle'}
			pendingLabel={message(language, 'saving')}
			onPress={() => void save()}
		/>
	</div>
</div>

<style>
	.edit {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.fields {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
		gap: var(--space-5);
	}

	.fields.wide {
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
	}

	.actions {
		display: flex;
		flex-wrap: wrap;
		justify-content: flex-end;
		gap: var(--touch-gap);
	}
</style>
